import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[2px] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-accent text-accent-foreground hover:brightness-110',
        destructive:
          'bg-destructive text-destructive-foreground',
        outline:
          'border border-input bg-transparent hover:bg-muted text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:brightness-95',
        ghost: 'border border-transparent hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline',
        whiteOutline: 'border border-white bg-transparent text-white hover:bg-white/10',
      },
      size: {
        default: 'h-10 px-4 py-2 text-[15px]',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-12 px-8 text-[17px]',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };